import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import EntityMember from "_shared/entity-members/entity-member";
import { useState } from "react";
import { InFlightRevisionDisposition, WorkTeamSide } from "work/work-agreements/teams/leader-handover/team-leader-handover";
import SuccessorTable, {
  SigningAuthorityGrant,
  SuccessorCandidate,
} from "work/work-agreements/teams/leader-handover/_views/successor-table";
import useSuccessorCandidates from "work/work-agreements/teams/leader-handover/_views/use-successor-candidates";
import { SESSION_RECORDING_MASK_CLASS } from "_shared/session-recording/session-recording-privacy";

export type LeaderHandoverChoice = {
  successor: EntityMember;
  outgoingLeaderStays: boolean;
  grantSigningAuthority: boolean;
  revisionDisposition: InFlightRevisionDisposition;
};

export type LeaderHandoverDialogProps = {
  open: boolean;
  side: WorkTeamSide;
  /** The leader being handed over from. */
  outgoingLeader: EntityMember;
  /** Everybody on the team now. */
  members: readonly EntityMember[];
  /** People on the other side of the work, who cannot lead this team. */
  excludedUserIds?: readonly string[];
  /**
   * Whether the reader may grant signing authority (`User.canGrantSigningAuthority`). Only they are
   * offered the grant that goes with choosing a colleague who lacks it; anybody else is told who can
   * grant it.
   */
  canGrantSigningAuthority: boolean;
  /**
   * The vendor leader's departure ends the work: a restricted team on hired work, or a leader who
   * leaves holding a rate. The dialog says so before they offer it.
   */
  endsTheWork?: boolean;
  /** A revision of this work is in progress, and the leader must say what happens to it. */
  hasRevisionInFlight?: boolean;
  /** A vendor team handover during negotiation also waits on the client team leader. */
  awaitsTheOtherSide?: boolean;
  /**
   * The client team leader is proposing a new vendor team leader. They cannot read the vendor's
   * firm, so only its people on the team are offered, and the vendor leader decides whether they
   * stay and whether to grant signing authority when they approve.
   */
  proposedByTheOtherSide?: boolean;
  submitting?: boolean;
  onSubmit: (choice: LeaderHandoverChoice) => void;
  onCancel: () => void;
  /** Offered when nobody can take the team over: a leader who cannot hand over ends the work instead. */
  onEndWorkInstead?: () => void;
};

/**
 * How a team leader leaves (#5836): they choose a colleague at their own firm -- on the team or
 * not -- to lead it instead, and say whether they stay on as a member. Nothing changes until the
 * successor accepts. A leader with nobody to hand over to is told so, and offered the one other way
 * out: ending the work.
 */
export default function LeaderHandoverDialog(props: Readonly<LeaderHandoverDialogProps>) {
  const { open, side, outgoingLeader, members, onSubmit, onCancel, onEndWorkInstead } = props;
  const proposedByTheOtherSide = props.proposedByTheOtherSide === true;
  // On a phone the colleagues table needs the whole screen: in a floating dialog it showed one
  // column and a sliver of the next, beside its own sideways scroll (#6721).
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const canGrant = props.canGrantSigningAuthority && !proposedByTheOtherSide;
  // A client leader proposing a new vendor leader grants nothing: the vendor leader says whether
  // they grant it when they approve, so the client may still propose a colleague without it.
  const signingAuthorityGrant: SigningAuthorityGrant = proposedByTheOtherSide
    ? "byTheOutgoingLeader"
    : canGrant
      ? "byYou"
      : "unavailable";
  const { candidates, isLoading, firmIsUnlisted, refresh } = useSuccessorCandidates({
    outgoingLeader,
    teamMembers: members,
    excludedUserIds: props.excludedUserIds ?? [],
    listWholeFirm: !proposedByTheOtherSide,
    joiningRequiresCredentials: side === WorkTeamSide.Vendor,
  });
  const [successorUserId, setSuccessorUserId] = useState<string | undefined>(undefined);
  const [outgoingLeaderStays, setOutgoingLeaderStays] = useState(true);
  // Ticked as each colleague is chosen, so choosing somebody without signing authority is the one
  // step that offers it (#6095). The leader may still untick it.
  const [grantSigningAuthority, setGrantSigningAuthority] = useState(true);
  const [revisionDisposition, setRevisionDisposition] = useState<InFlightRevisionDisposition>(
    InFlightRevisionDisposition.None,
  );

  const successor = candidates.find((candidate) => candidate.member.userId.value === successorUserId);
  const successorName = successor?.name ?? "a colleague";
  // Unknown is not lacking, but a grant to somebody who turns out to hold it is ignored, so it is
  // offered for them too, and only blocks the offer for somebody known to lack it.
  const successorMayLackSigningAuthority = successor !== undefined && successor.hasSigningAuthority !== true;
  const successorLacksSigningAuthority = successor?.hasSigningAuthority === false;
  const teamName = side === WorkTeamSide.Client ? "client team" : "vendor team";
  const owesRevisionChoice = props.hasRevisionInFlight === true && revisionDisposition === InFlightRevisionDisposition.None;
  const owesGrant = successorLacksSigningAuthority && canGrant && !grantSigningAuthority;
  const nobodyHereCanGrant = successorLacksSigningAuthority && signingAuthorityGrant === "unavailable";
  const successorLacksCredentials = successor?.credentials === "missing";
  const canSubmit =
    successor !== undefined &&
    !owesRevisionChoice &&
    !owesGrant &&
    !nobodyHereCanGrant &&
    !successorLacksCredentials &&
    !props.submitting;
  const nobodyToHandOverTo = !isLoading && candidates.length === 0;

  function choose(candidate: SuccessorCandidate) {
    setSuccessorUserId(candidate.member.userId.value);
    setGrantSigningAuthority(true);
  }

  function submit() {
    if (!successor) return;
    onSubmit({
      successor: successor.member,
      outgoingLeaderStays,
      grantSigningAuthority: successorMayLackSigningAuthority && canGrant && grantSigningAuthority,
      revisionDisposition: props.hasRevisionInFlight ? revisionDisposition : InFlightRevisionDisposition.None,
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      aria-labelledby="leader-handover-title"
    >
      <DialogTitle id="leader-handover-title">
        {proposedByTheOtherSide ? `Propose a new ${teamName} leader` : `Hand over the ${teamName}`}
      </DialogTitle>
      <DialogContent>
        {nobodyToHandOverTo ? (
          <Stack spacing={2}>
            <Typography>
              Nobody else can lead the {teamName}. Only a colleague from the same firm can take it over.
            </Typography>
            <Typography>Invite a colleague to your firm first, or end the work.</Typography>
          </Stack>
        ) : (
          <Stack spacing={3}>
            <Typography>
              {proposedByTheOtherSide
                ? `Choose who you would like to lead the ${teamName}. They must accept, and its current leader must approve, before anything changes.`
                : `Choose who leads the ${teamName} after you. They must accept before anything changes${props.awaitsTheOtherSide ? ", and the client team leader must approve" : ""}.`}
            </Typography>
            {firmIsUnlisted && (
              <Alert severity="info">
                You cannot see your firm&apos;s whole roster, so only colleagues already on the team are listed.
              </Alert>
            )}

            <FormControl>
              <FormLabel id="leader-handover-successor-label">New leader</FormLabel>
              <SuccessorTable
                candidates={candidates}
                selectedUserId={successorUserId}
                onSelect={choose}
                signingAuthorityGrant={signingAuthorityGrant}
                showCredentials={side === WorkTeamSide.Vendor}
                isLoading={isLoading}
              />
            </FormControl>

            {successorMayLackSigningAuthority && canGrant && (
              <Alert severity={owesGrant ? "warning" : "info"}>
                <Typography variant="body2">
                  {successorLacksSigningAuthority
                    ? `${successorName} does not hold signing authority for your firm. A team leader signs the work, so they need it.`
                    : `You cannot see whether ${successorName} holds signing authority for your firm. A team leader signs the work, so they need it. If they already hold it, nothing is granted.`}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={grantSigningAuthority}
                      onChange={(event) => setGrantSigningAuthority(event.target.checked)}
                    />
                  }
                  label={`Grant ${successorName} signing authority for your firm when they accept`}
                />
                {owesGrant && (
                  <Typography variant="body2">
                    You can offer the team only with the grant, or to somebody who holds signing authority.
                  </Typography>
                )}
              </Alert>
            )}

            {nobodyHereCanGrant && (
              <Alert severity="warning">
                {successorName} does not hold signing authority for your firm, and a team leader needs it to sign the
                work. You cannot grant it. Somebody with Member Permissions for your whole firm can grant it on your
                firm&apos;s Permissions page. Then offer {successorName} the team, or choose somebody who holds it now.
              </Alert>
            )}

            {successorLacksCredentials && (
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" size="small" onClick={refresh}>
                    Check again
                  </Button>
                }
              >
                {successorName} has no professional credentials on their profile. Everybody who joins a vendor team
                needs them, and {successorName} joins it by leading it. Ask them to add their credentials to their
                profile, then offer them the team. They do not have to publish the profile.
              </Alert>
            )}

            {!proposedByTheOtherSide && (
              <FormControl>
                <FormLabel id="leader-handover-stay-label">After the handover</FormLabel>
                <RadioGroup
                  aria-labelledby="leader-handover-stay-label"
                  value={outgoingLeaderStays ? "stay" : "leave"}
                  onChange={(event) => setOutgoingLeaderStays(event.target.value === "stay")}
                >
                  <FormControlLabel value="stay" control={<Radio />} label="Stay on the team as a member" />
                  <FormControlLabel value="leave" control={<Radio />} label="Leave the team" />
                </RadioGroup>
              </FormControl>
            )}

            {props.hasRevisionInFlight && (
              <FormControl>
                <Alert severity="info" sx={{ mb: 1 }}>
                  A revision of this work is being negotiated. If you opened it, choose what happens to it. A revision
                  the other side opened carries on with the new leader either way.
                </Alert>
                <FormLabel id="leader-handover-revision-label">The revision you opened</FormLabel>
                <RadioGroup
                  aria-labelledby="leader-handover-revision-label"
                  value={revisionDisposition}
                  onChange={(event) => setRevisionDisposition(event.target.value as InFlightRevisionDisposition)}
                >
                  <FormControlLabel
                    value={InFlightRevisionDisposition.HandOver}
                    control={<Radio />}
                    label={successor ? `Hand it over to ${successorName}, drafts and all` : "Hand it over, drafts and all"}
                  />
                  <FormControlLabel
                    value={InFlightRevisionDisposition.Cancel}
                    control={<Radio />}
                    label="Cancel it. Its drafts and edits stay in its history."
                  />
                </RadioGroup>
              </FormControl>
            )}

            {props.endsTheWork && (
              <Alert severity="warning">
                This handover changes a term of the agreement, because the team is restricted or you hold a rate on its fee
                schedule. The work ends when the new leader accepts, and a new proposal they lead opens for them to
                negotiate with the client.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        {nobodyToHandOverTo && onEndWorkInstead ? (
          <Button color="error" variant="contained" onClick={onEndWorkInstead}>
            End the work
          </Button>
        ) : (
          // Names the successor, so it stays masked in session replays where other buttons do not (#5168).
          <Button variant="contained" disabled={!canSubmit} onClick={submit} className={SESSION_RECORDING_MASK_CLASS}>
            {proposedByTheOtherSide ? "Propose" : successor ? `Offer the team to ${successorName}` : "Offer the team"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
